import SceneConversation from 'components/Panel/Chat/Conversation/SceneConversation';
import { focusChatInput } from 'components/Panel/Chat/hooks/chatInputFocus';
import { Loader } from 'components/Widget/Loader';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectSceneChatRefs } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import {
	closeOtherSceneChatTabs,
	closeSceneChatTab,
	setActiveSceneChatId,
} from 'redux/mode/actions';
import {
	displayViewOnlyModal,
	replaceSceneChatWithDraft,
	startSceneChatDraft,
} from 'redux/mode/util';
import { RootReducer, selectActiveSceneChatOrDraft, selectSceneChatTabs } from 'redux/reducers';
import { Header } from '../Header';
import * as Base from '../styles';

const SceneChatPanel = () => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const {
		chatRefs,
		chatRefsLoaded,
		tabs,
		activeSceneChatId,
		activeScene,
		activeChat,
		activeChatIsEmpty,
		draftChatId,
		chatsLoading,
	} = useSelector((r: RootReducer) => {
			const chat = selectActiveSceneChatOrDraft(r);
			const chatRefs = selectSceneChatRefs(r.db);
			const activeSceneChatId = r.mode.activeSceneChatId;

			return {
				chatRefs,
				chatRefsLoaded: r.db.loading.activeScene.chatRefs === 'loaded',
				tabs: selectSceneChatTabs(r),
				activeSceneChatId,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				activeChat: chat,
				// An empty chat is already a new chat, so "+" has nothing to add.
				activeChatIsEmpty: !!chat && chat.entries.length === 0,
				// A brand-new chat is a client-only draft, so it is intentionally absent
				// from chatRefs and has no server row to rename or copy an id from.
				draftChatId: r.mode.draftSceneChat?.id ?? null,
				// The loader means "there is nothing to show", not "a request is in
				// flight". Whenever we hold a chat — a draft, an optimistic insert, or a
				// polled one — render it, so a chat that is mid-sync never blinks out.
				chatsLoading: activeSceneChatId !== null && chat === null,
			};
		},
		shallowEqual
	);
	const sortedChatRefs = useMemo(() => {
		// Chat refs are already sorted by updatedAt DESC from the backend
		return chatRefs;
	}, [chatRefs]);

	const buildChatMenuItems = useCallback(
		(chatId: string) => {
			if (chatId === draftChatId) return [];
			return [
				MenuItemAction.createRenameChat(chatId),
				MenuItemAction.createSelectFramesFromChat(chatId),
				MenuItemAction.createCopyChatId(chatId),
			];
		},
		[draftChatId]
	);

	// Falls back to the newest chat when the active one resolves to nothing. Holding a
	// chat is the signal to leave it alone: a draft or a freshly created chat is
	// legitimately missing from chatRefs, and reselecting would throw it away.
	// Only select once this scene's chat list has loaded — an unloaded list can't
	// say which chat is newest.
	useEffect(() => {
		if (activeChat) return;
		if (!chatRefsLoaded) return;

		const chatRefIds = sortedChatRefs.map((ref) => ref.id);
		const activeChatIdInRefs = activeSceneChatId ? chatRefIds.includes(activeSceneChatId) : false;

		if (!activeSceneChatId || !activeChatIdInRefs) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveSceneChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, chatRefsLoaded, activeSceneChatId, dispatch, activeChat]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const isEditable = (): boolean => {
		if (!activeScene) return false;
		if (!canEdit(activeScene)) {
			displayViewOnlyModal();
			return false;
		}
		return true;
	};

	// One click always lands the user typing in an empty chat. An empty chat that is
	// already open IS that chat, so it is focused rather than duplicated — which caps
	// stray clicks at the one empty tab.
	const onNewChat = () => {
		if (!isEditable()) return;
		if (draftChatId) {
			dispatch(setActiveSceneChatId(draftChatId));
		} else if (!activeChatIsEmpty || !activeSceneChatId) {
			// The new chat is a client-only draft; it hits the server only once its first
			// message is sent. We intentionally do NOT touch the pending chat input here;
			// the ChatInput component owns it.
			startSceneChatDraft();
		}
		focusChatInput();
	};

	const onReplaceTabWithNewChat = (targetId: string) => {
		if (!isEditable()) return;
		replaceSceneChatWithDraft(targetId);
		focusChatInput();
	};

	return (
		<Base.Base ref={ref}>
			<Header
				tabs={tabs}
				activeChatId={activeSceneChatId}
				chats={sortedChatRefs}
				onSelectChat={(id: string) => dispatch(setActiveSceneChatId(id))}
				onNewChat={onNewChat}
				onCloseTab={(id: string) => dispatch(closeSceneChatTab(id))}
				onCloseOtherTabs={(id: string) => dispatch(closeOtherSceneChatTabs(id))}
				onReplaceTabWithNewChat={onReplaceTabWithNewChat}
				buildChatMenuItems={buildChatMenuItems}
			/>
			{activeSceneChatId && (
				<Base.Conversation>
					{chatsLoading ? (
						<Base.LoadingContainer>
							<Loader size={32} />
						</Base.LoadingContainer>
					) : (
						<SceneConversation />
					)}
				</Base.Conversation>
			)}
		</Base.Base>
	);
};

export default SceneChatPanel;
